import styled from '@emotion/styled';
import { layers } from 'app/spacing';
import { createPortal } from 'react-dom';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  useCallback,
} from 'react';
import type { RefObject } from 'react';
import Loader from '__components/Kizen/Loader';
import css from '@emotion/css';
import Draggable from 'react-draggable';
import { Button } from '@kizen/kds/Button';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PluginContext } from 'ts-components/Plugins/context';
import { CirclePortal } from '../FloatingCircles';
import { useMutation } from '@tanstack/react-query';
import TeamMemberService from 'services/TeamMemberService';
import { EmployeeConfigMap, FloatingFrame } from './types';
import { useFloatingFrame } from '@kizenapps/engine/react';
import { WindowPosition } from '@kizenapps/engine';
import { getStableHash } from '@kizenapps/engine/util';
import { useTheme } from 'hooks/useTheme';

const DEFAULT_POSITION_GAP = 20;
const FRAME_HEADER_SIZE = 40;
const Z_START = layers.content(10500, 20) + 2; // z-index for the date/time picker, plus 2 so it's higher

const FloatWrapper = styled.div`
  width: ${({ width }: any) => width ?? 300}px;
  box-shadow: 0px 0px 10px 0px
    color-mix(in srgb, var(--color-background-black) 10%, transparent);
  border-radius: 4px;
  overflow: hidden;
  ${({ pending }: any) =>
    pending
      ? css`
          pointer-events: none;
          opacity: 0;
        `
      : ''}
  ${({ minimized }: any) =>
    minimized
      ? css`
          .floating-window-content {
            display: none;
          }
        `
      : ''}
    ${({ dragging, height }: any) =>
    !dragging
      ? css`
          transition: transform calc(${height ?? 600} * 0.0005s);
        `
      : css``}
`;
const AbsoluteLoader = styled(Loader)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background: var(--color-background-white);
`;

const ContentTarget = styled.div`
  width: 100%;
  opacity: 1;

  ${({ dragging }: any) =>
    dragging
      ? css`
          pointer-events: none;
        `
      : css`
          pointer-events: auto;
        `}

  ${({ scroll }: any) =>
    scroll
      ? css`
          overflow: auto;
        `
      : ''}

  ${({ solid }: any) =>
    solid
      ? css`
          background-color: var(--color-background-white);
        `
      : ''}
`;

const Parent = styled.div`
  position: fixed;
  top: ${({ navbarHeight }: any) => navbarHeight || 0}px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  ${({ hidden }: any) =>
    hidden
      ? css`
          opacity: 0;
          pointer-events: none;
        `
      : ''}

  ${({ dragging, offset = 0, totalOffset = 1 }) =>
    dragging
      ? css`
          pointer-events: auto;
          z-index: ${Z_START + 1 + totalOffset};
          user-select: none;
        `
      : css`
          pointer-events: none;
          z-index: ${Z_START + 1 + offset};
          user-select: auto;
        `}
`;

const FloatingWindowContent = styled.div`
  ${({ hiddenByModal }: any) =>
    hiddenByModal
      ? css`
          opacity: 0;
          pointer-events: none;
        `
      : css`
          opacity: 1;
          pointer-events: auto;
        `}
  // While the AI block editor is open. visibility (unlike opacity) also
  // hides and disables the frames and circles that set their own
  // pointer-events, and keeps them mounted so they come back as they were
  ${({ hiddenByEditor }: any) =>
    hiddenByEditor
      ? css`
          visibility: hidden;
        `
      : ''}
`;

const ScriptUITarget = styled.div`
  ${({ height }: { height?: number }) =>
    height
      ? css`
          min-height: ${height}px;
          height: ${height}px;
        `
      : css`
          height: 100%;
        `}
`;

const MinimizeController = styled.div`
  height: ${({ minimized, height }: any) => (minimized ? 0 : height ?? 600)}px;
  transition: height calc(${({ height }) => height ?? 600} * 0.0005s);
  overflow: hidden;
`;

const FloatingCircles = ({
  floatingFrames,
}: {
  floatingFrames: FloatingFrame[];
}) => {
  const circleFrames = floatingFrames.filter(
    (frame) => frame.minimized_style === 'circle'
  );
  return (
    <div
      className="flex w-full h-full pointer-events-none flex-col fixed left-0 right-0 bottom-0 top-0"
      style={{ zIndex: Z_START }}
    >
      <div className="floating-window-trigger-parent-left absolute flex flex-col fixed bottom-0 left-0 w-[100px] p-[15px]">
        {circleFrames.map((frame) => {
          const id = `${frame.plugin_api_name}-${frame.api_name}-trigger-left`;
          return <div key={id} id={id} />;
        })}
      </div>
      <div className="floating-window-trigger-parent-right absolute pointer-events-none flex flex-col fixed right-0 bottom-0 p-[15px]">
        {circleFrames.map((frame) => {
          const id = `${frame.plugin_api_name}-${frame.api_name}-trigger-right`;
          return <div key={id} id={id} />;
        })}
      </div>
    </div>
  );
};

const FloatingWindowChild = ({
  index,
  id,
  hiddenByModal,
  triggerElement,
  viewportHeight,
}: {
  index: number;
  id: string;
  hiddenByModal: boolean;
  triggerElement: string;
  viewportHeight: number;
}) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const floatingRef = useRef<HTMLDivElement>(null);

  const { floatingFrames, refetch } = useContext(PluginContext);

  const currentWindow = useMemo(() => {
    const window = floatingFrames?.[index];

    return window;
  }, [floatingFrames, index]);

  const mutateConfig = useMutation({
    mutationFn: async (value: {
      config: Record<string, EmployeeConfigMap>;
    }) => {
      const res = await TeamMemberService.updatePluginConfig(
        currentPluginId,
        value
      );

      return res;
    },
    onSettled: () => {
      refetch();
    },
  });

  const maybeUpdateEmployeeConfig = useCallback(
    (id: string, minimized: boolean, windowPosition?: WindowPosition | null) =>
      (value: EmployeeConfigMap) => {
        const oldConfigHash = getStableHash(
          currentWindow?.employee_config ?? {}
        );

        const newPayload = {
          ...currentWindow?.employee_config?.[id],
          minimized,
          ...value,
        };

        if (windowPosition) {
          newPayload.position = {
            left: windowPosition.left,
            top: windowPosition.top,
            quadrant: windowPosition.quadrant,
            deltas: windowPosition.deltas,
          };
        }

        if (value.position) {
          newPayload.position = value.position;
        }

        const newConfig = {
          ...currentWindow?.employee_config,
          [id]: newPayload,
        };

        const newConfigHash = getStableHash(newConfig);

        if (oldConfigHash === newConfigHash) {
          return;
        }

        mutateConfig.mutateAsync({ config: newConfig });
      },
    [mutateConfig, currentWindow]
  );

  const {
    circleProps,
    parentProps,
    draggableProps,
    floatProps,
    contentProps,
    pending,
    indicator,
    outputUIRef,
    scopedCss,
    sanitizedHtml,
    interactableScriptRef,
    tooltipPosition,
    hideHeader,
    setMinimized,
    currentPluginId,
    dragHandleClassName,
    isFixed,
    isCircle,
    minimized,
    height,
    script,
    dragging,
    frameOffset,
    scriptUIRef,
  } = useFloatingFrame({
    currentWindow,
    pathname,
    id,
    frameHeaderSize: FRAME_HEADER_SIZE,
    defaultPositionGap: DEFAULT_POSITION_GAP,
    viewportHeight,
    hiddenByModal,
    updateEmployeeConfig: maybeUpdateEmployeeConfig,
  });

  return (
    <>
      <CirclePortal
        triggerElement={triggerElement}
        zIndex={Z_START}
        offset={frameOffset}
        {...circleProps}
      />
      <Parent
        offset={frameOffset}
        totalOffset={floatingFrames.length}
        data-qa-minimized={minimized}
        {...parentProps}
      >
        <Draggable {...draggableProps}>
          <FloatWrapper
            className="floating-window flex flex-col"
            ref={floatingRef}
            {...floatProps}
          >
            {pending || indicator === 'block' ? (
              <AbsoluteLoader loading />
            ) : null}
            {!hideHeader ? (
              <div
                className="kds w-full pr-[1rem] flex items-center justify-between bg-[var(--frame-header-color)] text-[var(--frame-header-text-color)] select-none pointer-events-auto"
                style={
                  {
                    '--frame-header-color':
                      currentWindow.header_color || 'white',
                    '--frame-header-text-color':
                      currentWindow.header_text_color || 'black',
                  } as any
                }
              >
                <div
                  className={`${isFixed ? '' : 'cursor-grab'} ${dragHandleClassName} h-[var(--frame-header-size)] px-[1rem] flex items-center`}
                  style={
                    {
                      '--frame-header-size': `${FRAME_HEADER_SIZE}px`,
                    } as any
                  }
                >
                  {isFixed ? null : <Icon icon="action-drag-handle" />}
                </div>
                <div className="max-w-[50%]">
                  <Typography truncate>{currentWindow.title}</Typography>
                </div>
                <div
                  className={'flex items-center w-[10px]'}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <Button
                    rightIcon={
                      minimized
                        ? 'action-expand-frame'
                        : 'action-collapse-frame'
                    }
                    variant="text"
                    color="inherit"
                    size="small"
                    onClick={() => setMinimized(!minimized)}
                    rightIconSettings={{
                      tooltipDescriptionOverride: minimized
                        ? t('Expand')
                        : t('Collapse'),
                      tooltipPosition: tooltipPosition,
                    }}
                  />
                </div>
              </div>
            ) : null}
            {currentWindow.type === 'script' && script ? (
              <ContentTarget {...contentProps}>
                <MinimizeController
                  height={height}
                  minimized={minimized}
                  isCircle={isCircle}
                >
                  <ScriptUITarget height={height} className="w-full">
                    <div
                      className="h-full w-full"
                      ref={scriptUIRef as RefObject<HTMLDivElement> | undefined}
                    />
                  </ScriptUITarget>
                  <style>{scopedCss}</style>
                </MinimizeController>
              </ContentTarget>
            ) : null}
            {currentWindow.type === 'html' && sanitizedHtml ? (
              <>
                <ContentTarget
                  ref={interactableScriptRef as RefObject<HTMLDivElement>}
                  dragging={dragging}
                  solid
                  scroll
                >
                  <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
                  <div ref={outputUIRef as RefObject<HTMLDivElement>} />

                  <style>{scopedCss}</style>
                </ContentTarget>
              </>
            ) : null}
          </FloatWrapper>
        </Draggable>
      </Parent>
    </>
  );
};

const FloatingWindowWrapper = () => {
  const { currentTheme } = useTheme();
  const { floatingFrames, hiddenByModal, hiddenByEditor, sessionInvalid } =
    useContext(PluginContext);
  const container = document.getElementById('floating-window-root');
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setViewportHeight(window.innerHeight);
    });

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!container || sessionInvalid) {
    return null;
  }

  return createPortal(
    <FloatingWindowContent
      key={currentTheme}
      hiddenByEditor={hiddenByEditor}
      data-qa-hidden-by-editor={hiddenByEditor}
    >
      <FloatingCircles floatingFrames={floatingFrames} />
      {floatingFrames.map((frame, index) => {
        const id = `${frame.plugin_api_name}-${frame.api_name}`;

        return (
          <FloatingWindowChild
            key={id}
            index={index}
            id={id}
            hiddenByModal={hiddenByModal}
            triggerElement={`${id}-trigger`}
            viewportHeight={viewportHeight}
          />
        );
      })}
    </FloatingWindowContent>,
    container
  );
};

export const FloatingWindow = () => {
  const { isLoading } = useContext(PluginContext);

  if (isLoading) {
    return null;
  }

  return <FloatingWindowWrapper />;
};
